import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Plus, Search } from "lucide-react";

export type AuthDropdownOption = {
  key: string | number;
  label: string;
};

type AuthDropdownProps = {
  label?: string;
  options: AuthDropdownOption[];
  value?: AuthDropdownOption | null;
  onChange: (option: AuthDropdownOption) => void;
  onOptionsChange?: (options: AuthDropdownOption[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  width?: string;
  others?: boolean;
  searchable?: boolean; // 👈 new prop
};

const OTHERS_KEY = "__others__";

const AuthDropdown: React.FC<AuthDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  onOptionsChange,
  placeholder = "Select an option",
  disabled = false,
  error,
  width = "100%",
  others = false,
  searchable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [showOthersInput, setShowOthersInput] = useState(false);
  const [othersText, setOthersText] = useState("");
  const [localOptions, setLocalOptions] =
    useState<AuthDropdownOption[]>(options);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Sync if parent options change
  useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
    // Reset search when dropdown closes
    if (!open) {
      setSearchQuery("");
    }
  }, [open, searchable]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setShowOthersInput(false);
        setOthersText("");
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showOthersInput) inputRef.current?.focus();
  }, [showOthersInput]);

  // Filtered options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return localOptions;
    const q = searchQuery.trim().toLowerCase();
    return localOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [searchable, searchQuery, localOptions]);

  const handleAddCustomOption = () => {
    const trimmed = othersText.trim();
    if (!trimmed) return;

    const newOption: AuthDropdownOption = {
      key: `${OTHERS_KEY}_${Date.now()}`,
      label: trimmed,
    };

    const updatedOptions = [...localOptions, newOption];
    setLocalOptions(updatedOptions);
    onOptionsChange?.(updatedOptions);
    onChange(newOption);

    setOpen(false);
    setShowOthersInput(false);
    setOthersText("");
    setSearchQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleAddCustomOption();
    if (e.key === "Escape") {
      setShowOthersInput(false);
      setOthersText("");
    }
  };

  return (
    <div ref={dropdownRef} className="flex flex-col gap-1.5" style={{ width }}>
      {label && (
        <label className="text-[#121217] text-sm font-medium">{label}</label>
      )}

      <div className="relative w-full">
        {/* Trigger Button */}
        <div
          onClick={() => !disabled && setOpen((p) => !p)}
          className={`
            auth-input-shadow
            px-4
            py-[12.5px]
            rounded-lg
            flex
            items-center
            justify-between
            cursor-pointer
            ${disabled ? "bg-[#F2F4F7] cursor-not-allowed" : "bg-white"}
            ${error ? "border border-red-500" : ""}
          `}
        >
          <p
            className={`text-sm tracking-normal truncate ${
              value ? "text-[#121217]" : "text-[#9CA3AF]"
            }`}
          >
            {value?.label || placeholder}
          </p>

          <ChevronDown
            size={16}
            className={`text-[#667085] transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>

        {/* Dropdown Panel */}
        {open && !disabled && (
          <div className="absolute z-50 top-full mt-1.5 w-full bg-white rounded-xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(0,0,0,0.08)] overflow-hidden">
            {/* Search Input — only when searchable=true */}
            {searchable && (
              <div className="p-2 border-b border-[#F3F4F6]">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB] focus-within:border-[#F97316] focus-within:shadow-[0_0_0_2px_rgba(249,115,22,0.1)] transition-all duration-200">
                  <Search size={14} className="text-[#9CA3AF] shrink-0" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full bg-transparent outline-none text-sm text-[#121217] placeholder-[#C1C9D2]"
                  />
                </div>
              </div>
            )}

            {/* Options List */}
            <div className="max-h-[200px] overflow-y-auto py-1">
              {filteredOptions.length === 0 && !others && (
                <p className="px-3.5 py-2.5 text-sm text-[#9CA3AF] text-center">
                  {searchable && searchQuery
                    ? "No results found."
                    : "No options available"}
                </p>
              )}

              {filteredOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                    setShowOthersInput(false);
                    setSearchQuery("");
                  }}
                  className={`
                    w-full flex items-center justify-between px-3.5 py-2 text-sm text-left
                    transition-colors duration-150
                    ${
                      value?.key === option.key
                        ? "bg-[#FFF7F0] text-[#F97316]"
                        : "text-[#374151] hover:bg-[#F9FAFB]"
                    }
                  `}
                >
                  <span>{option.label}</span>
                  {value?.key === option.key && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] shrink-0" />
                  )}
                </button>
              ))}

              {/* Show "no results" message when searching with others enabled */}
              {searchable &&
                searchQuery &&
                filteredOptions.length === 0 &&
                others && (
                  <p className="px-3.5 py-2.5 text-sm text-[#9CA3AF] text-center">
                    No results found.
                  </p>
                )}
            </div>

            {/* Others Section */}
            {others && (
              <div className="border-t border-[#F3F4F6]">
                {!showOthersInput ? (
                  <button
                    type="button"
                    onClick={() => setShowOthersInput(true)}
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-xs lg:text-sm text-[#9CA3AF] hover:text-[#F97316] hover:bg-[#FFF7F0] transition-colors duration-150"
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    <span>Add custom option</span>
                  </button>
                ) : (
                  <div className="p-2.5 flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={othersText}
                      onChange={(e) => setOthersText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type and press Enter..."
                      className="flex-1 text-sm text-[#121217] placeholder-[#C1C9D2] bg-[#F9FAFB] border border-[#E5E7EB] focus:border-[#F97316] focus:shadow-[0_0_0_2px_rgba(249,115,22,0.1)] rounded-lg px-3 py-1.5 outline-none transition-all duration-200 w-[80%]"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomOption}
                      disabled={!othersText.trim()}
                      className="shrink-0 p-1.5 rounded-lg bg-[#F97316] hover:bg-[#EA6C0A] disabled:bg-[#F3F4F6] disabled:cursor-not-allowed transition-colors duration-150"
                    >
                      <Plus
                        size={15}
                        strokeWidth={2.5}
                        className={
                          othersText.trim() ? "text-white" : "text-[#C1C9D2]"
                        }
                      />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    </div>
  );
};

export default AuthDropdown;
